import React, { useState, useEffect } from 'react';
import Button from './Button';
import Icon from '../AppIcon';


const DuplicateSongModal = ({
  isOpen,
  onClose,
  duplicates = [],
  originalData = {},
  onMerge,
  onCreateNew,
  title = "Duplicate Songs Found"
}) => {
  const [selectedSong, setSelectedSong] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (duplicates?.length > 0) {
      setSelectedSong(duplicates?.[0]);
    }
  }, [duplicates]);

  useEffect(() => {
    if (selectedSong && isOpen) {
      loadComparison();
    }
  }, [selectedSong, isOpen]);

  const loadComparison = async () => {
    if (!selectedSong) return;
    
    setLoading(true);
    try {
      // Create a temporary comparison by simulating what the new song would look like
      const comparison = {
        song1: {
          id: 'new',
          title: originalData?.title || '',
          musical_title: originalData?.musical_title || '',
          act_number: originalData?.act_number || null,
          scene_number: originalData?.scene_number || null,
          song_order: originalData?.song_order || null,
          tempo: originalData?.tempo || '',
          key_signature: originalData?.key_signature || '',
          time_signature: originalData?.time_signature || '4/4',
          duration_seconds: originalData?.duration_seconds || null,
          lyrics: originalData?.lyrics || '',
          created_at: new Date()?.toISOString()
        },
        song2: {
          id: selectedSong?.song_id || selectedSong?.id,
          title: selectedSong?.title || '',
          musical_title: selectedSong?.musical_title || '',
          act_number: selectedSong?.act_number || null,
          scene_number: selectedSong?.scene_number || null,
          song_order: selectedSong?.song_order || null,
          tempo: selectedSong?.tempo || '',
          key_signature: selectedSong?.key_signature || '',
          time_signature: selectedSong?.time_signature || '4/4',
          duration_seconds: selectedSong?.duration_seconds || null,
          lyrics: selectedSong?.lyrics || '',
          created_at: selectedSong?.created_at || ''
        }
      };

      setComparisonData(comparison);
    } catch (error) {
      console.error('Error loading comparison:', error);
      setComparisonData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (!selectedSong) return;
    
    try {
      await onMerge(selectedSong);
      handleClose();
    } catch (error) {
      console.error('Error merging songs:', error);
    }
  };

  const handleCreateNew = () => {
    onCreateNew();
    handleClose();
  };

  const handleClose = () => {
    setSelectedSong(null);
    setComparisonData(null);
    setLoading(false);
    onClose();
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 italic">Not specified</span>;
    }
    return <span className="text-gray-900">{value}</span>;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'Not specified';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-gray-900">
              {title}
            </h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Summary */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-900 mb-1">
                  Found {duplicates?.length} potential duplicate{duplicates?.length !== 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-yellow-800">
                  The song "{originalData?.title}" appears to already exist in the database. 
                  You can merge with an existing song or create a new entry.
                </p>
              </div>
            </div>
          </div>

          {/* Duplicate Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Select Song to Compare
            </h3>
            <div className="grid gap-2 max-h-32 overflow-y-auto">
              {duplicates?.map((duplicate) => (
                <button
                  key={duplicate?.song_id || duplicate?.id}
                  onClick={() => setSelectedSong(duplicate)}
                  className={`p-3 text-left border rounded-lg transition-colors ${
                    (selectedSong?.song_id || selectedSong?.id) === (duplicate?.song_id || duplicate?.id)
                      ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{duplicate?.title}</div>
                      <div className="text-sm text-gray-600">
                        {duplicate?.musical_title && `Musical: ${duplicate?.musical_title}`}
                        {duplicate?.act_number && ` • Act ${duplicate?.act_number}`}
                        {duplicate?.scene_number && ` Scene ${duplicate?.scene_number}`}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div className="text-xs">
                        Match: {Math.round((duplicate?.similarity_score || 0) * 100)}%
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Comparison */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={32} className="animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading comparison...</span>
            </div>
          )}

          {comparisonData && !loading && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Detailed Comparison
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* New Song Data */}
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                    <Icon name="Plus" size={16} className="mr-2" />
                    New Song (from Import/Form)
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Title:</label>
                      <div className="mt-1">{formatFieldValue(comparisonData?.song1?.title)}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Musical:</label>
                      <div className="mt-1">{formatFieldValue(comparisonData?.song1?.musical_title)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Act:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song1?.act_number)}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Scene:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song1?.scene_number)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tempo:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song1?.tempo)}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Key:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song1?.key_signature)}</div>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Duration:</label>
                      <div className="mt-1">{formatFieldValue(formatDuration(comparisonData?.song1?.duration_seconds))}</div>
                    </div>
                  </div>
                </div>

                {/* Existing Song */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Icon name="Database" size={16} className="mr-2" />
                    Existing Song
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Title:</label>
                      <div className="mt-1">{formatFieldValue(comparisonData?.song2?.title)}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Musical:</label>
                      <div className="mt-1">{formatFieldValue(comparisonData?.song2?.musical_title)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Act:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song2?.act_number)}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Scene:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song2?.scene_number)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tempo:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song2?.tempo)}</div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Key:</label>
                        <div className="mt-1">{formatFieldValue(comparisonData?.song2?.key_signature)}</div>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Duration:</label>
                      <div className="mt-1">{formatFieldValue(formatDuration(comparisonData?.song2?.duration_seconds))}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            
            <Button
              variant="default"
              onClick={handleCreateNew}
              iconName="Plus"
              iconPosition="left"
              className="flex-1"
            >
              Create New Song
            </Button>

            {selectedSong && (
              <Button
                variant="primary"
                onClick={handleMerge}
                iconName="GitMerge"
                iconPosition="left"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                Keep Existing Song
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateSongModal;